import { useEffect, useState, useMemo } from "react";
import { causasFalla, causasIncumplimiento, mapaAgrupacion } from "./causas";
import { puedeEditarModulo, esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";

function OfensoresN2({ usuario }) {

  const [data, setData] = useState([]);
  const [dataEdicion, setDataEdicion] = useState({}); // cambios locales mientras se edita
  const [busquedaNodo, setBusquedaNodo] = useState("");
  const [filtroNodo, setFiltroNodo] = useState("");
  const [filtroAliado, setFiltroAliado] = useState([]);
  const [filtroMes, setFiltroMes] = useState([]);
  const [filtroCiudad, setFiltroCiudad] = useState([]);
  const [filtroJefe, setFiltroJefe] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState([]);
  const [filtroCausaFalla, setFiltroCausaFalla] = useState([]);

  const [paginaActual, setPaginaActual] = useState(1);
  const FILAS_POR_PAGINA = 50;

  // Vista congelada: se activa al editar y solo se libera cuando el usuario
  // cambia un filtro externo manualmente (no al editar campos de la tabla)
  const [vistaCongelada, setVistaCongelada] = useState(null);
  const [conciliando, setConciliando] = useState({}); // { "id_nodo|mes": true/false }
  const [conciliadosMap, setConciliadosMap] = useState({}); // { key: id_conciliacion }

  // Ordenamiento de columnas
  const [sortCol, setSortCol]   = useState("");
  const [sortDir, setSortDir]   = useState("asc");

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPaginaActual(1);
  };

  // Permisos centralizados
  const puedeEditar = puedeEditarModulo(usuario, "ofensores_n2");
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);

  const normalizar = (txt) =>
    (txt ?? "").toString().replace(/\s+/g, " ").trim().toLowerCase();

  // Helpers para cambiar filtros externos — siempre descongelan la vista
  const cambiarFiltro = (setter) => (val) => { setVistaCongelada(null); setter(val); };
  const setBusquedaNodoF    = cambiarFiltro(setBusquedaNodo);
  const setFiltroNodoF      = cambiarFiltro(setFiltroNodo);
  const setFiltroAliadoF    = cambiarFiltro(setFiltroAliado);
  const setFiltroMesF       = cambiarFiltro(setFiltroMes);
  const setFiltroCiudadF    = cambiarFiltro(setFiltroCiudad);
  const setFiltroJefeF      = cambiarFiltro(setFiltroJefe);
  const setFiltroCategoriaF = cambiarFiltro(setFiltroCategoria);
  const setFiltroCausaF     = cambiarFiltro(setFiltroCausaFalla);

  const formatearMes = (fecha) => {
    if (!fecha) return "";
    if (fecha.includes("T")) {
      const [anio, mes] = fecha.split("T")[0].split("-");
      return `${mes}-${anio}`;
    }
    if (fecha.includes("-") && fecha.length === 10) {
      const [anio, mes] = fecha.split("-");
      return `${mes}-${anio}`;
    }
    return fecha;
  };

  // ✅ Fetch con rol y cédula para que el backend filtre según perfil
  useEffect(() => {
    const rol     = encodeURIComponent(usuario?.rol || "");
    const cedula  = encodeURIComponent(usuario?.cedula || "");
    const aliado  = encodeURIComponent(usuario?.aliado || "");
    fetch(`${process.env.REACT_APP_API_URL}/ofensores-n2?rol=${rol}&cedula=${cedula}&aliado=${aliado}`)
      .then(res => res.json())
      .then(data => setData(data))
      .catch(err => console.error("Error cargando ofensores:", err));
  }, [usuario]);

  // Normaliza cualquier fecha a YYYY-MM para usar como key consistente
  const normalizarMes = (f) => {
    if (!f) return "";
    const s = f.toString();
    // ISO con T: "2026-02-01T00:00:00.000Z" → "2026-02"
    if (s.includes("T")) return s.split("T")[0].substring(0, 7);
    // "2026-02-01" → "2026-02"
    if (s.length >= 7) return s.substring(0, 7);
    return s;
  };

  // Cargar nodos ya conciliados para marcarlos en la tabla
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/conciliaciones`)
      .then(res => res.json())
      .then(rows => {
        const map = {};
        rows.forEach(r => { map[`${r.id_nodo}|${normalizarMes(r.mes_anio)}`] = r.id; });
        setConciliadosMap(map);
      })
      .catch(() => {});
  }, []);

  const toggleConciliar = (item, nuevoValor) => {
    const key     = `${item.id_nodo}|${normalizarMes(item.mes_anio || item.fecha_corte)}`;
    const idExist = conciliadosMap[key];
    setConciliando(prev => ({ ...prev, [key]: true }));

    if (nuevoValor === "SI") {
      // ── Agregar a conciliaciones ──
      fetch(`${process.env.REACT_APP_API_URL}/conciliaciones`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_nodo:                  item.id_nodo,
          NOMBRE_NODO:              item.NOMBRE_NODO,
          ciudad:                   item.ciudad,
          categoria:                item.categoria,
          porc_dispo_real:          item.porc_dispo_real,
          causa_falla:              item.causa_falla,
          causa_incumplimiento_icr: item.causa_incumplimiento_icr,
          observaciones:            item.observaciones,
          tipo_prioridad:           item.tipo_prioridad || item.prioridad,
          mes_anio:                 normalizarMes(item.mes_anio || item.fecha_corte) + "-01",
          agrupacion:               item.agrupacion,
          aliado:                   item.aliado,
          nombre_jefe:              item.nombre_jefe,
          cedula_usuario:           usuario?.cedula,
          nombre_usuario:           usuario?.nombre,
        })
      })
      .then(res => res.json())
      .then(resp => {
        if (resp.ok) setConciliadosMap(prev => ({ ...prev, [key]: resp.id }));
        else console.error("Error conciliando:", resp.mensaje);
      })
      .catch(err => console.error("Error:", err))
      .finally(() => setConciliando(prev => { const n = {...prev}; delete n[key]; return n; }));

    } else {
      // ── Quitar de conciliaciones (No Aplica) ──
      if (!idExist) { setConciliando(prev => { const n = {...prev}; delete n[key]; return n; }); return; }
      fetch(`${process.env.REACT_APP_API_URL}/conciliaciones/${idExist}`, { method: "DELETE" })
      .then(res => res.json())
      .then(resp => {
        if (resp.ok) setConciliadosMap(prev => { const n = {...prev}; delete n[key]; return n; });
      })
      .catch(err => console.error("Error:", err))
      .finally(() => setConciliando(prev => { const n = {...prev}; delete n[key]; return n; }));
    }
  };

  // Clave unica por registro: id_nodo + fecha_corte
  const getKey = (id_nodo, fecha_corte) => `${id_nodo}||${fecha_corte || ""}`;

  // Actualiza solo visualmente mientras el usuario edita (sin tocar filtros)
  const actualizarLocal = (id_nodo, fecha_corte, campo, valor) => {
    if (!puedeEditar) return;
    // Al empezar a editar, congelar la vista actual si no está ya congelada
    if (!vistaCongelada) setVistaCongelada(datosFiltrados);
    const key = getKey(id_nodo, fecha_corte);
    let nuevaAgrupacion = null;
    if (campo === "causa_falla") {
      nuevaAgrupacion = mapaAgrupacion[valor] || "SIN CLASIFICAR";
    }
    setDataEdicion(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [campo]: valor,
        ...(nuevaAgrupacion ? { agrupacion: nuevaAgrupacion } : {})
      }
    }));
  };

  // Confirma el cambio en data real y envía al backend (al soltar el campo)
  const confirmarCambio = (id_nodo, fecha_corte_item, campo) => {
    if (!puedeEditar) return;
    const key = getKey(id_nodo, fecha_corte_item);
    const edicion = dataEdicion[key];
    if (!edicion || edicion[campo] === undefined) return;

    const valor = edicion[campo];
    const registro = data.find(item =>
      item.id_nodo === id_nodo &&
      (item.fecha_corte || "") === (fecha_corte_item || "")
    );
    if (!registro) return;
    const nuevaAgrupacion = edicion.agrupacion || registro.agrupacion;

    // Actualizar data real solo para este registro exacto (id_nodo + fecha_corte)
    setData(prev =>
      prev.map(item =>
        item.id_nodo === id_nodo && item.fecha_corte === fecha_corte_item
          ? { ...item, [campo]: valor, agrupacion: nuevaAgrupacion }
          : item
      )
    );

    // Limpiar edicion local de este campo
    setDataEdicion(prev => {
      const nuevo = { ...prev };
      if (nuevo[key]) delete nuevo[key][campo];
      if (nuevo[key] && Object.keys(nuevo[key]).length === 0) delete nuevo[key];
      return nuevo;
    });

    // Reflejar el cambio en la vista congelada para que se vea en la tabla
    if (vistaCongelada) {
      setVistaCongelada(prev => prev.map(item =>
        item.id_nodo === id_nodo && item.fecha_corte === fecha_corte_item
          ? { ...item, [campo]: valor, agrupacion: nuevaAgrupacion }
          : item
      ));
    }

    // Enviar al backend con fecha_corte para identificar registro exacto
    const fechaCorte = fecha_corte_item
      ? (fecha_corte_item.includes("T") ? fecha_corte_item.split("T")[0] : fecha_corte_item.substring(0, 10))
      : null;

    fetch(`${process.env.REACT_APP_API_URL}/ofensores/${id_nodo}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        causa_falla:              campo === "causa_falla"              ? valor : (edicion?.causa_falla              ?? registro.causa_falla),
        causa_incumplimiento_icr: campo === "causa_incumplimiento_icr" ? valor : (edicion?.causa_incumplimiento_icr ?? registro.causa_incumplimiento_icr),
        observaciones:            campo === "observaciones"            ? valor : (edicion?.observaciones            ?? registro.observaciones),
        fecha_corte:              fechaCorte,
        rol:                      usuario?.rol,
        cedula:                   usuario?.cedula,
        nombre_usuario:           usuario?.nombre,
      })
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(err => {
          console.error("❌ Error guardando cambio:", err?.mensaje || res.status);
        });
      }
      return res.json().then(resp => {
        if (resp?.agrupacion_generada) {
          setData(prev =>
            prev.map(item =>
              item.id_nodo === id_nodo && item.fecha_corte === fecha_corte_item
                ? { ...item, agrupacion: resp.agrupacion_generada }
                : item
            )
          );
        }
      });
    })
    .catch(err => console.error("❌ Error de red al guardar:", err));
  };

  const descargarCSV = () => {
    const encabezados = [
      "ID Nodo", "Nodo", "Ciudad", "Categoría", "% Dispo Real", "Horas Indisponibilidad",
      "Causa Falla", "Causa Incumplimiento", "Prioridad", "Mes", "Agrupación",
      "Aliado", "Jefe", "Observaciones"
    ];
    const filas = datosFiltrados.map(item => {
      const porcDispo = item.porc_dispo_real != null ? parseFloat(item.porc_dispo_real) : null;
      const horasIndispo = porcDispo != null
        ? (() => {
            const fechaMes = item.mes_anio ? new Date(item.mes_anio) : new Date();
            const diasDelMes = new Date(fechaMes.getFullYear(), fechaMes.getMonth() + 1, 0).getDate();
            const horasTotalesMes = diasDelMes * 24;
            return (horasTotalesMes - porcDispo * horasTotalesMes / 100).toFixed(1);
          })()
        : "";
      return [
        item.id_nodo, item.NOMBRE_NODO, item.ciudad, item.categoria,
        porcDispo != null ? `${porcDispo.toFixed(2)}%` : "",
        horasIndispo ? `${horasIndispo} h` : "",
        item.causa_falla || "", item.causa_incumplimiento_icr || "",
        item.tipo_prioridad || "", formatearMes(item.mes_anio),
        item.agrupacion || "", item.aliado || "",
        item.nombre_jefe || "", item.observaciones || ""
      ];
    });
    const csvContenido = [encabezados, ...filas]
      .map(fila => fila.map(celda =>
        `"${(celda ?? "").toString().replace(/"/g, '""')}"`
      ).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContenido], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ofensores_n2_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── FILTRO BASE: aplica todos los filtros activos a data completa ──────
  // Esta es la fuente de verdad para la tabla Y para las opciones de cada filtro
  const datosFiltrados = useMemo(() => {
    setPaginaActual(1);
    return data.filter(item => {
      const mes      = formatearMes(item.mes_anio);
      const aliado   = (item.aliado      || "").trim();
      const ciudad   = (item.ciudad      || "").trim();
      const jefe     = (item.nombre_jefe || "").trim();
      const categoria= (item.categoria   || "").trim();
      const nodo     = (item.id_nodo     || "").trim();
      const causaVal = (item.causa_falla || "").trim();
      const causaKey = causaVal === "" ? "(sin causa)" : causaVal;
      return (
        (busquedaNodo        === "" || nodo.toLowerCase().includes(busquedaNodo.toLowerCase().trim())) &&
        (filtroNodo          === "" || nodo === filtroNodo) &&
        (filtroMes.length    === 0  || filtroMes.includes(mes)) &&
        (filtroAliado.length === 0  || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length === 0  || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length   === 0  || filtroJefe.includes(jefe)) &&
        (filtroCategoria.length === 0 || filtroCategoria.includes(categoria)) &&
        (filtroCausaFalla.length === 0 || filtroCausaFalla.some(f =>
          f === "(sin causa)" ? causaKey === "(sin causa)" : causaKey.toLowerCase() === f.toLowerCase()
        ))
      );
    });
  }, [data, busquedaNodo, filtroNodo, filtroMes, filtroAliado, filtroCiudad, filtroJefe, filtroCategoria, filtroCausaFalla]);

  // ── OPCIONES BIDIRECCIONALES ─────────────────────────────────────────────
  // Cada filtro muestra las opciones que existen considerando TODOS los demás
  // filtros activos (excepto el suyo propio). Así al seleccionar Jefe, Ciudad
  // y Aliado también se reducen a lo que corresponde a ese jefe.

  // Datos sin filtro de Mes → opciones de Mes
  const opcionesMes = useMemo(() => {
    const base = data.filter(i => {
      const aliado   = (i.aliado      || "").trim();
      const ciudad   = (i.ciudad      || "").trim();
      const jefe     = (i.nombre_jefe || "").trim();
      const categoria= (i.categoria   || "").trim();
      const nodo     = (i.id_nodo     || "").trim();
      return (
        (filtroNodo          === "" || nodo === filtroNodo) &&
        (filtroAliado.length === 0  || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length === 0  || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length   === 0  || filtroJefe.includes(jefe)) &&
        (filtroCategoria.length === 0 || filtroCategoria.includes(categoria))
      );
    });
    return [...new Set(base.map(i => formatearMes(i.mes_anio)))].filter(Boolean)
      .sort((a, b) => { const [mA,aA]=a.split("-"); const [mB,aB]=b.split("-"); return new Date(aB,mB-1)-new Date(aA,mA-1); });
  }, [data, filtroNodo, filtroAliado, filtroCiudad, filtroJefe, filtroCategoria]);

  // Datos sin filtro de Aliado → opciones de Aliado
  const opcionesAliado = useMemo(() => {
    const base = data.filter(i => {
      const mes      = formatearMes(i.mes_anio);
      const ciudad   = (i.ciudad      || "").trim();
      const jefe     = (i.nombre_jefe || "").trim();
      const categoria= (i.categoria   || "").trim();
      const nodo     = (i.id_nodo     || "").trim();
      return (
        (filtroMes.length    === 0  || filtroMes.includes(mes)) &&
        (filtroNodo          === "" || nodo === filtroNodo) &&
        (filtroCiudad.length === 0  || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length   === 0  || filtroJefe.includes(jefe)) &&
        (filtroCategoria.length === 0 || filtroCategoria.includes(categoria))
      );
    });
    return [...new Set(base.map(i => (i.aliado || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroNodo, filtroCiudad, filtroJefe, filtroCategoria]);

  // Datos sin filtro de Ciudad → opciones de Ciudad
  const opcionesCiudad = useMemo(() => {
    const base = data.filter(i => {
      const mes      = formatearMes(i.mes_anio);
      const aliado   = (i.aliado      || "").trim();
      const jefe     = (i.nombre_jefe || "").trim();
      const categoria= (i.categoria   || "").trim();
      const nodo     = (i.id_nodo     || "").trim();
      return (
        (filtroMes.length    === 0  || filtroMes.includes(mes)) &&
        (filtroNodo          === "" || nodo === filtroNodo) &&
        (filtroAliado.length === 0  || filtroAliado.includes(aliado)) &&
        (filtroJefe.length   === 0  || filtroJefe.includes(jefe)) &&
        (filtroCategoria.length === 0 || filtroCategoria.includes(categoria))
      );
    });
    return [...new Set(base.map(i => (i.ciudad || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroNodo, filtroAliado, filtroJefe, filtroCategoria]);

  // Datos sin filtro de Jefe → opciones de Jefe
  const opcionesJefe = useMemo(() => {
    const base = data.filter(i => {
      const mes      = formatearMes(i.mes_anio);
      const aliado   = (i.aliado   || "").trim();
      const ciudad   = (i.ciudad   || "").trim();
      const categoria= (i.categoria|| "").trim();
      const nodo     = (i.id_nodo  || "").trim();
      return (
        (filtroMes.length    === 0  || filtroMes.includes(mes)) &&
        (filtroNodo          === "" || nodo === filtroNodo) &&
        (filtroAliado.length === 0  || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length === 0  || filtroCiudad.includes(ciudad)) &&
        (filtroCategoria.length === 0 || filtroCategoria.includes(categoria))
      );
    });
    return [...new Set(base.map(i => (i.nombre_jefe || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroNodo, filtroAliado, filtroCiudad, filtroCategoria]);

  // Datos sin filtro de Categoría → opciones de Categoría
  const opcionesCategoria = useMemo(() => {
    const base = data.filter(i => {
      const mes    = formatearMes(i.mes_anio);
      const aliado = (i.aliado      || "").trim();
      const ciudad = (i.ciudad      || "").trim();
      const jefe   = (i.nombre_jefe || "").trim();
      const nodo   = (i.id_nodo     || "").trim();
      return (
        (filtroMes.length    === 0  || filtroMes.includes(mes)) &&
        (filtroNodo          === "" || nodo === filtroNodo) &&
        (filtroAliado.length === 0  || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length === 0  || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length   === 0  || filtroJefe.includes(jefe))
      );
    });
    return [...new Set(base.map(i => (i.categoria || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroNodo, filtroAliado, filtroCiudad, filtroJefe]);

  // Opciones de Nodo — depende de todos los filtros activos
  const opcionesNodo = useMemo(() =>
    [...new Set(datosFiltrados.map(i => (i.id_nodo || "").trim()))].filter(Boolean).sort()
  , [datosFiltrados]);
  // ────────────────────────────────────────────────────────────────────────

  // La tabla usa la vista congelada si existe, si no los datos filtrados normales
  const datosParaTabla = vistaCongelada ?? datosFiltrados;

  const datosOrdenados = useMemo(() => {
    if (!sortCol) return datosParaTabla;
    return [...datosParaTabla].sort((a, b) => {
      let valA = a[sortCol] ?? "";
      let valB = b[sortCol] ?? "";
      // Numeric sort for categoria
      if (sortCol === "categoria") {
        const numA = parseInt((valA || "").replace(/[^0-9]/g, "")) || 0;
        const numB = parseInt((valB || "").replace(/[^0-9]/g, "")) || 0;
        return sortDir === "asc" ? numA - numB : numB - numA;
      }
      // String sort
      valA = valA.toString().toLowerCase();
      valB = valB.toString().toLowerCase();
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [datosParaTabla, sortCol, sortDir]);

  const totalPaginas = Math.ceil(datosOrdenados.length / FILAS_POR_PAGINA);
  const datosPagina  = datosOrdenados.slice(
    (paginaActual - 1) * FILAS_POR_PAGINA,
    paginaActual * FILAS_POR_PAGINA
  );

  const totalOfensores = datosFiltrados.length;
  const totalAlto      = datosFiltrados.filter(i => normalizar(i.tipo_prioridad).includes("alto")).length;
  const totalMedio     = datosFiltrados.filter(i => normalizar(i.tipo_prioridad).includes("medio")).length;
  const sinGestionar   = datosFiltrados.filter(i => !i.causa_falla || i.causa_falla.trim() === "").length;

  // ── Resumen de pendientes sin causa por jefe (sobre datosFiltrados, respeta filtros activos) ──
  const resumenPendientesPorJefe = useMemo(() => {
    const map = {};
    datosFiltrados.forEach(item => {
      const jefe = (item.nombre_jefe || "Sin jefe").trim();
      if (!map[jefe]) map[jefe] = { total: 0, sinCausa: 0 };
      map[jefe].total++;
      if (!item.causa_falla || item.causa_falla.trim() === "") map[jefe].sinCausa++;
    });
    return Object.entries(map)
      .map(([jefe, v]) => ({ jefe, total: v.total, sinCausa: v.sinCausa, pct: v.total > 0 ? Math.round((v.sinCausa / v.total) * 100) : 0 }))
      .filter(r => r.sinCausa > 0)
      .sort((a, b) => b.sinCausa - a.sinCausa);
  }, [datosFiltrados]);

  const btnStyle = (activo) => ({
    padding: "6px 10px", borderRadius: "6px",
    border: "1px solid #cbd5e1",
    background: activo ? "#1e293b" : "#fff",
    color: activo ? "#fff" : "#1e293b",
    cursor: "pointer",
    fontWeight: activo ? "bold" : "normal"
  });

  const inputReadOnly = {
    width: "100%", fontSize: "12px",
    background: "#f1f5f9", color: "#64748b",
    border: "1px solid #e2e8f0", borderRadius: "4px",
    padding: "4px 6px", cursor: "not-allowed"
  };

  // Obtiene el valor actual del campo: edicion local si existe, sino el de data
  const getVal = (item, campo) => {
    const key = getKey(item.id_nodo, item.fecha_corte);
    return dataEdicion[key]?.[campo] !== undefined
      ? dataEdicion[key][campo]
      : item[campo];
  };

  const thSort = (label, col, minWidth) => (
    <th
      onClick={() => handleSort(col)}
      style={{
        padding: "8px", cursor: "pointer", userSelect: "none",
        background: sortCol === col ? "#2d3f55" : "#1e293b",
        color: "white", whiteSpace: "nowrap",
        borderBottom: sortCol === col ? "2px solid #E30613" : "none",
        ...(minWidth ? { minWidth } : {})
      }}
    >
      {label}
      {sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
    </th>
  );

  return (
    <div style={{ fontFamily: "Arial" }}>

      <div style={{ padding: "20px 20px 0 20px", maxWidth: "1400px" }}>
      <h2 style={{ margin: "0 0 16px 0" }}>🔴 Panel Ofensores N2+</h2>

      {/* Aviso solo lectura */}
      {!puedeEditar && (
        <div style={{
          background: "#fef9c3", border: "1px solid #fde68a",
          borderRadius: "8px", padding: "10px 16px",
          marginBottom: "16px", fontSize: "13px", color: "#92400e",
          display: "flex", alignItems: "center", gap: "8px"
        }}>
          👁️ <strong>Modo consulta:</strong> Solo puedes visualizar la información, no puedes modificarla.
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 20px", flex: "1 1 160px", minWidth: "160px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "14px", borderLeft: "4px solid #0f172a" }}>
          <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>📡</div>
          <div>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Total N2+</div>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#0f172a", lineHeight: 1.1 }}>{totalOfensores}</div>
          </div>
        </div>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 20px", flex: "1 1 160px", minWidth: "160px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "14px", borderLeft: "4px solid #dc2626" }}>
          <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>🔴</div>
          <div>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Prioridad Alto</div>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#dc2626", lineHeight: 1.1 }}>{totalAlto}</div>
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "1px" }}>{totalOfensores > 0 ? Math.round((totalAlto / totalOfensores) * 100) : 0}% del total</div>
          </div>
        </div>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 20px", flex: "1 1 160px", minWidth: "160px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "14px", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>🟡</div>
          <div>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Prioridad Medio</div>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#d97706", lineHeight: 1.1 }}>{totalMedio}</div>
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "1px" }}>{totalOfensores > 0 ? Math.round((totalMedio / totalOfensores) * 100) : 0}% del total</div>
          </div>
        </div>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 20px", flex: "1 1 160px", minWidth: "160px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "14px", borderLeft: sinGestionar > 0 ? "4px solid #7c3aed" : "4px solid #e2e8f0" }}>
          <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: sinGestionar > 0 ? "#f5f3ff" : "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>⚠️</div>
          <div>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Sin Gestionar</div>
            <div style={{ fontSize: "28px", fontWeight: "800", color: sinGestionar > 0 ? "#7c3aed" : "#94a3b8", lineHeight: 1.1 }}>{sinGestionar}</div>
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "1px" }}>{sinGestionar > 0 ? `${Math.round((sinGestionar / totalOfensores) * 100)}% sin causa` : "Todo gestionado ✓"}</div>
          </div>
        </div>
      </div>

      {/* TABLA RESUMEN PENDIENTES POR JEFE */}
      {resumenPendientesPorJefe.length > 0 && (
        <div style={{ background: "white", border: "1px solid #fecaca", borderRadius: "12px", padding: "16px 20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <div style={{ width: "3px", height: "18px", background: "#E30613", borderRadius: "2px" }} />
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Pendientes de validar por Jefe de Zona
            </span>
            <span style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "2px 10px", fontSize: "11px", color: "#E30613", fontWeight: "700" }}>
              {resumenPendientesPorJefe.reduce((s, r) => s + r.sinCausa, 0)} sin causa
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#0f172a" }}>
                  <th style={{ padding: "8px 12px", color: "white", textAlign: "left", fontWeight: "600", fontSize: "11px", whiteSpace: "nowrap" }}>Jefe de Zona</th>
                  <th style={{ padding: "8px 12px", color: "white", textAlign: "center", fontWeight: "600", fontSize: "11px" }}>Total N2+</th>
                  <th style={{ padding: "8px 12px", color: "white", textAlign: "center", fontWeight: "600", fontSize: "11px" }}>Sin causa asignada</th>
                  <th style={{ padding: "8px 12px", color: "white", textAlign: "center", fontWeight: "600", fontSize: "11px" }}>% Pendiente</th>
                  <th style={{ padding: "8px 12px", color: "white", textAlign: "left", fontWeight: "600", fontSize: "11px", minWidth: "160px" }}>Avance</th>
                  <th style={{ padding: "8px 12px", color: "white", textAlign: "center", fontWeight: "600", fontSize: "11px" }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {resumenPendientesPorJefe.map((row, i) => {
                  const pctGestionado = 100 - row.pct;
                  const colorBarra = row.pct >= 50 ? "#E30613" : row.pct >= 20 ? "#f59e0b" : "#16a34a";
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 12px", fontWeight: "600", color: "#0f172a" }}>{row.jefe}</td>
                      <td style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>{row.total}</td>
                      <td style={{ padding: "8px 12px", textAlign: "center" }}>
                        <span style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", padding: "2px 10px", color: "#E30613", fontWeight: "700", fontSize: "13px" }}>
                          {row.sinCausa}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "center" }}>
                        <span style={{ fontWeight: "700", color: colorBarra }}>{row.pct}%</span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                            <div style={{ width: `${pctGestionado}%`, height: "100%", background: colorBarra === "#16a34a" ? "#16a34a" : colorBarra, borderRadius: "4px", transition: "width .3s" }} />
                          </div>
                          <span style={{ fontSize: "11px", color: "#64748b", minWidth: "32px" }}>{pctGestionado}%</span>
                        </div>
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "center" }}>
                        <button
                          onClick={() => { setVistaCongelada(null); setFiltroJefe([row.jefe]); setFiltroCausaFalla(["(sin causa)"]); }}
                          style={{ background: "#E30613", color: "white", border: "none", borderRadius: "6px", padding: "4px 12px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                          Ver pendientes
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BANNER VISTA CONGELADA */}
      {vistaCongelada && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px",
          padding: "10px 16px", marginBottom: "12px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "16px" }}>🔒</span>
            <span style={{ fontSize: "13px", color: "#92400e", fontWeight: "600" }}>
              Vista congelada — los registros se mantienen visibles mientras terminas de editar.
              Cambia un filtro o haz clic en Refrescar para actualizar la tabla.
            </span>
          </div>
          <button
            onClick={() => setVistaCongelada(null)}
            style={{ background: "#f59e0b", color: "white", border: "none", borderRadius: "7px", padding: "6px 14px", fontSize: "12px", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap" }}>
            🔄 Refrescar vista
          </button>
        </div>
      )}

      {/* FILTROS */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px 20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "3px", height: "18px", background: "#E30613", borderRadius: "2px" }} />
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Filtros</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#475569", fontWeight: "600" }}>
              {datosFiltrados.length} registros
            </div>
            {(filtroNodo || filtroAliado.length > 0 || filtroCiudad.length > 0 || filtroMes.length > 0 || filtroJefe.length > 0 || filtroCategoria.length > 0 || filtroCausaFalla.length > 0 || busquedaNodo) && (
              <button onClick={() => { setVistaCongelada(null); setFiltroNodo(""); setFiltroAliado([]); setFiltroCiudad([]); setFiltroMes([]); setFiltroJefe([]); setFiltroCategoria([]); setFiltroCausaFalla([]); setBusquedaNodo(""); }}
                style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#E30613", fontWeight: "600", cursor: "pointer" }}>
                ✕ Limpiar filtros
              </button>
            )}
            <button onClick={descargarCSV} style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
              ⬇️ Descargar CSV
            </button>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-start" }}>

          {/* Buscar nodo — ancho fijo pequeño */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "140px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Buscar nodo</label>
            <input type="text" placeholder="Escribe un nodo..." value={busquedaNodo} onChange={(e) => setBusquedaNodoF(e.target.value)}
              style={{ padding: "8px 10px", borderRadius: "7px", border: busquedaNodo ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", outline: "none", width: "100%", boxSizing: "border-box" }} />
          </div>

          {/* Mes — contenido corto MM-YYYY, ancho ajustado */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "110px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Mes</label>
            <select multiple size={Math.min(opcionesMes.length, 5)} value={filtroMes}
              onChange={(e) => setFiltroMesF(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroMes.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              {opcionesMes.map((m, i) => <option key={i} value={m}>{m}</option>)}
            </select>
            {filtroMes.length > 0 && <button onClick={() => setFiltroMesF([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Aliado — CONECTAR TV / TABASCO */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "130px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Aliado</label>
            <select multiple size={Math.min(opcionesAliado.length, 5)} value={filtroAliado}
              onChange={(e) => setFiltroAliadoF(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroAliado.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              {opcionesAliado.map((a, i) => <option key={i} value={a}>{a}</option>)}
            </select>
            {filtroAliado.length > 0 && <button onClick={() => setFiltroAliadoF([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Ciudad — nombres medianos */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "160px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Ciudad</label>
            <select multiple size={Math.min(opcionesCiudad.length, 5)} value={filtroCiudad}
              onChange={(e) => setFiltroCiudadF(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroCiudad.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              {opcionesCiudad.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
            {filtroCiudad.length > 0 && <button onClick={() => setFiltroCiudadF([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Jefe — nombres largos, más ancho */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "220px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Jefe Zona</label>
            <select multiple size={Math.min(opcionesJefe.length, 5)} value={filtroJefe}
              onChange={(e) => setFiltroJefeF(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroJefe.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              {opcionesJefe.map((j, i) => <option key={i} value={j}>{j}</option>)}
            </select>
            {filtroJefe.length > 0 && <button onClick={() => setFiltroJefeF([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Categoría — N2, N3, N4... muy corto */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "80px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Categoría</label>
            <select multiple size={Math.min(opcionesCategoria.length, 5)} value={filtroCategoria}
              onChange={(e) => setFiltroCategoriaF(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroCategoria.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              {opcionesCategoria.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
            {filtroCategoria.length > 0 && <button onClick={() => setFiltroCategoriaF([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Nodo — select simple */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "120px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Nodo</label>
            <select value={filtroNodo} onChange={(e) => setFiltroNodoF(e.target.value)}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroNodo ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              <option value="">Todos</option>
              {opcionesNodo.map((n, i) => <option key={i} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Causa Falla — texto largo, ancho generoso */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "340px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Causa Falla
              {data.some(i => !i.causa_falla || i.causa_falla.trim() === "") && (
                <span style={{ marginLeft: "6px", background: "#fef9c3", border: "1px solid #fde68a", color: "#92400e", borderRadius: "4px", padding: "1px 5px", fontSize: "9px" }}>⚠️ hay sin asignar</span>
              )}
            </label>
            <select multiple size={5} value={filtroCausaFalla}
              onChange={(e) => setFiltroCausaF(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroCausaFalla.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%" }}>
              <option value="(sin causa)" style={{ color: "#b45309", fontStyle: "italic", background: "#fef9c3" }}>⚠️ Sin causa asignada</option>
              {causasFalla.map((c, i) => <option key={i} value={c.toLowerCase()}>{c}</option>)}
            </select>
            {filtroCausaFalla.length > 0 && <button onClick={() => setFiltroCausaF([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

        </div>

        {(filtroNodo || filtroAliado.length > 0 || filtroCiudad.length > 0 || filtroMes.length > 0 || filtroJefe.length > 0 || filtroCategoria.length > 0 || filtroCausaFalla.length > 0 || busquedaNodo) && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", alignSelf: "center" }}>Activos:</span>
            {[
              { label: busquedaNodo,                               clear: () => setBusquedaNodo(""),  prefix: "Buscar: " },
              { label: filtroNodo,                                 clear: () => setFiltroNodo(""),    prefix: "ID: " },
              ...(filtroMes.map(v       => ({ label: v, clear: () => setFiltroMesF(p => p.filter(x => x !== v)),       prefix: "Mes: " }))),
              ...(filtroAliado.map(v    => ({ label: v, clear: () => setFiltroAliadoF(p => p.filter(x => x !== v)),    prefix: "Aliado: " }))),
              ...(filtroCiudad.map(v    => ({ label: v, clear: () => setFiltroCiudadF(p => p.filter(x => x !== v)),    prefix: "Ciudad: " }))),
              ...(filtroJefe.map(v      => ({ label: v, clear: () => setFiltroJefeF(p => p.filter(x => x !== v)),      prefix: "Jefe: " }))),
              ...(filtroCategoria.map(v => ({ label: v, clear: () => setFiltroCategoriaF(p => p.filter(x => x !== v)), prefix: "Cat: " }))),
              ...(filtroCausaFalla.map(v=> ({ label: v === "(sin causa)" ? "Sin causa" : v, clear: () => setFiltroCausaF(p => p.filter(x => x !== v)), prefix: "Causa: " }))),
            ].filter(f => f.label).map((f, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", color: "#E30613", fontWeight: "600" }}>
                {f.prefix}{f.label}
                <button onClick={f.clear} style={{ background: "none", border: "none", cursor: "pointer", color: "#E30613", fontSize: "12px", padding: 0, lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      </div>{/* end maxWidth wrapper */}

      {/* TABLA */}
      <div style={{ overflowX: "auto", padding: "0 20px" }}>
        <table border="1" style={{ marginTop: "8px", width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead style={{ background: "#1e293b", color: "white", position: "sticky", top: "0px", zIndex: 10 }}>
            <tr>
              {thSort("ID Nodo",            "id_nodo")}
              {thSort("Nodo",               "NOMBRE_NODO")}
              {thSort("Ciudad",             "ciudad")}
              {thSort("Categoria",          "categoria")}
              {thSort("% Dispo Real",       "porc_dispo_real")}
              <th style={{ padding: "8px", minWidth: "120px", background: "#1e293b", color: "white", cursor: "default" }}>Horas Indisponibilidad</th>
              {thSort("Causa Falla", "causa_falla", "280px")}
              {thSort("Causa Incumplimiento", "causa_incumplimiento_icr", "280px")}
              {thSort("Prioridad",          "tipo_prioridad")}
              {thSort("Mes",                "mes_anio")}
              {thSort("Agrupacion",         "agrupacion")}
              {thSort("Aliado",             "aliado")}
              {thSort("Jefe",               "nombre_jefe")}
              <th style={{ padding: "8px", minWidth: "200px", background: "#1e293b", color: "white" }}>Observaciones</th>
              <th style={{ padding: "8px", minWidth: "110px", background: "#1e293b", color: "white", textAlign: "center" }}>Conciliar</th>
            </tr>
          </thead>
          <tbody>
            {datosPagina.map((item, index) => {
              const opcionesFalla = causasFalla.includes(item.causa_falla)
                ? causasFalla
                : [item.causa_falla, ...causasFalla].filter(Boolean);
              const valorActual = normalizar(item.causa_incumplimiento_icr);
              const valorEnCatalogo = causasIncumplimiento.find(c => normalizar(c) === valorActual) || "";

              return (
                <tr key={index} style={{ background: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
                  <td style={{ padding: "6px 8px" }}>{item.id_nodo}</td>
                  <td style={{ padding: "6px 8px" }}>{item.NOMBRE_NODO}</td>
                  <td style={{ padding: "6px 8px" }}>{item.ciudad}</td>
                  <td style={{ padding: "6px 8px" }}>{item.categoria}</td>

                  {/* % Disponibilidad Real */}
                  <td style={{ padding: "6px 8px", textAlign: "center" }}>
                    {item.porc_dispo_real != null
                      ? `${parseFloat(item.porc_dispo_real).toFixed(2)}%`
                      : "—"}
                  </td>

                  {/* Horas de Indisponibilidad (calculado) */}
                  <td style={{ padding: "6px 8px", textAlign: "center" }}>
                    {item.porc_dispo_real != null
                      ? (() => {
                          // Calcular horas totales del mes según fecha real del registro
                          const fechaMes = item.mes_anio ? new Date(item.mes_anio) : new Date();
                          const diasDelMes = new Date(fechaMes.getFullYear(), fechaMes.getMonth() + 1, 0).getDate();
                          const horasTotalesMes = diasDelMes * 24;
                          const horas = horasTotalesMes - parseFloat(item.porc_dispo_real) * horasTotalesMes / 100;
                          return `${horas.toFixed(1)} h`;
                        })()
                      : "—"}
                  </td>

                  {/* Causa Falla */}
                  <td style={{ padding: "6px 8px", minWidth: "280px" }}>
                    {puedeEditar ? (
                      <select value={getVal(item, "causa_falla") || ""} onChange={(e) => actualizarLocal(item.id_nodo, item.fecha_corte, "causa_falla", e.target.value)} onBlur={(e) => confirmarCambio(item.id_nodo, item.fecha_corte, "causa_falla")} style={{ width: "100%", fontSize: "12px", minWidth: "260px" }}>
                        <option value="">-- Seleccionar --</option>
                        {opcionesFalla.map((c, i) => <option key={i} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <span style={inputReadOnly}>{item.causa_falla || "—"}</span>
                    )}
                  </td>

                  {/* Causa Incumplimiento */}
                  <td style={{ padding: "6px 8px", minWidth: "280px" }}>
                    {puedeEditar ? (
                      <select value={getVal(item, "causa_incumplimiento_icr") ? (causasIncumplimiento.find(c => (c||"").toLowerCase() === (getVal(item, "causa_incumplimiento_icr")||"").toLowerCase()) || "") : ""} onChange={(e) => actualizarLocal(item.id_nodo, item.fecha_corte, "causa_incumplimiento_icr", e.target.value)} onBlur={(e) => confirmarCambio(item.id_nodo, item.fecha_corte, "causa_incumplimiento_icr")} style={{ width: "100%", fontSize: "12px", minWidth: "260px" }}>
                        <option value="">-- Seleccionar --</option>
                        {causasIncumplimiento.map((c, i) => <option key={i} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <span style={inputReadOnly}>{item.causa_incumplimiento_icr || "—"}</span>
                    )}
                  </td>

                  <td style={{ padding: "6px 8px" }}>{item.tipo_prioridad || item.prioridad}</td>
                  <td style={{ padding: "6px 8px" }}>{formatearMes(item.mes_anio)}</td>
                  <td style={{ padding: "6px 8px" }}>{item.agrupacion}</td>
                  <td style={{ padding: "6px 8px" }}>{item.aliado}</td>
                  <td style={{ padding: "6px 8px" }}>{item.nombre_jefe}</td>

                  {/* Observaciones */}
                  <td style={{ padding: "6px 8px" }}>
                    {puedeEditar ? (
                      <textarea value={getVal(item, "observaciones") || ""} onChange={(e) => actualizarLocal(item.id_nodo, item.fecha_corte, "observaciones", e.target.value)} onBlur={(e) => confirmarCambio(item.id_nodo, item.fecha_corte, "observaciones")}
                        rows={2} style={{ width: "100%", fontSize: "12px", resize: "vertical", borderRadius: "4px", border: "1px solid #cbd5e1", padding: "4px" }} placeholder="Escribir observación..." />
                    ) : (
                      <span style={{ ...inputReadOnly, display: "block", minHeight: "36px", whiteSpace: "pre-wrap" }}>{item.observaciones || "—"}</span>
                    )}
                  </td>
                  {/* Conciliar */}
                  {(() => {
                    const key          = `${item.id_nodo}|${normalizarMes(item.mes_anio || item.fecha_corte)}`;
                    const yaConciliado = key in conciliadosMap;
                    const cargando     = conciliando[key];
                    return (
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        <select
                          disabled={cargando}
                          value={yaConciliado ? "SI" : "No Aplica"}
                          onChange={(e) => toggleConciliar(item, e.target.value)}
                          style={{
                            fontSize: "12px", padding: "4px 6px", borderRadius: "6px",
                            border: yaConciliado ? "1.5px solid #16a34a" : "1.5px solid #e2e8f0",
                            background: cargando ? "#f1f5f9" : yaConciliado ? "#dcfce7" : "white",
                            color: yaConciliado ? "#16a34a" : "#475569",
                            fontWeight: yaConciliado ? "700" : "400",
                            cursor: cargando ? "not-allowed" : "pointer",
                            width: "100%", minWidth: "90px"
                          }}>
                          <option value="No Aplica">No Aplica</option>
                          <option value="SI">SI</option>
                        </select>
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINACIÓN */}
      <div style={{ padding: "0 20px" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "16px", gap: "4px", flexWrap: "wrap" }}>
          <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} style={btnStyle(false)}>« Primera</button>
          <button onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1} style={btnStyle(false)}>‹ Anterior</button>
          {Array.from({ length: totalPaginas }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPaginas || Math.abs(p - paginaActual) <= 2)
            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
            .map((p, i) => p === "..." ? (
              <span key={i} style={{ padding: "6px 4px", color: "#94a3b8" }}>…</span>
            ) : (
              <button key={i} onClick={() => setPaginaActual(p)} style={btnStyle(p === paginaActual)}>{p}</button>
            ))}
          <button onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas} style={btnStyle(false)}>Siguiente ›</button>
          <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual === totalPaginas} style={btnStyle(false)}>Última »</button>
        </div>

        {/* Info de página y registros */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: "12px", marginBottom: "24px",
          padding: "10px 16px",
          background: "white", borderRadius: "10px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          fontSize: "13px", color: "#64748b",
          flexWrap: "wrap", gap: "8px",
        }}>
          <span>
            📄 Página <strong style={{ color: "#0f172a" }}>{paginaActual}</strong> de <strong style={{ color: "#0f172a" }}>{totalPaginas}</strong>
          </span>
          <span style={{ color: "#94a3b8" }}>|</span>
          <span>
            Mostrando <strong style={{ color: "#0f172a" }}>{(paginaActual - 1) * FILAS_POR_PAGINA + 1}</strong>–<strong style={{ color: "#0f172a" }}>{Math.min(paginaActual * FILAS_POR_PAGINA, datosOrdenados.length)}</strong> de <strong style={{ color: "#E30613" }}>{datosOrdenados.length}</strong> registros filtrados
          </span>
          {datosOrdenados.length !== data.length && (
            <>
              <span style={{ color: "#94a3b8" }}>|</span>
              <span style={{ color: "#94a3b8" }}>
                Total en base: <strong style={{ color: "#0f172a" }}>{data.length}</strong>
              </span>
            </>
          )}
        </div>
      </div>

    </div>
  );
}

export default OfensoresN2;